import React from "react";
import toast from "react-hot-toast";
import { Printer } from "react-feather";
import { PRINT_BASE, PRINT_TD, PRINT_TH, PrintHeader, printElement } from "../../../../../components/common/printSheet";
import { isGroupLine, quotationLineTypeLabel } from "../../components/QuotationLineLabel";

/*
  Print one Quotation Prototype (user, 2026-09-19: "add a print option for quot prototype").

  A prototype is an internal template, not a customer document - so this is a plain working
  sheet over the shared popup plumbing, NOT the letterheaded quotation PDF (which needs a
  customer, a signature and terms a prototype does not have). Same reading as the detail
  page: a product-group line leads with its GROUP, and the customer wording sits under it
  only when it says something different.
*/

const money = (n) => Number(n || 0).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const same = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
const RIGHT = { textAlign: "right", whiteSpace: "nowrap" };

const lineNet = (l) => Number(l.qty || 0) * Number(l.rate || 0);
const lineVat = (l) => (lineNet(l) * Number(l.vatPercent || 0)) / 100;

const ItemCell = ({ line }) => {
  if (isGroupLine(line)) {
    const groupName = line.productGroupName || `Group ${line.productGroupId}`;
    return (
      <>
        <div style={{ fontWeight: 700 }}>{groupName}</div>
        {line.description && !same(line.description, groupName) && (
          <div style={{ fontSize: "9.5px", color: "#444" }}>{line.description}</div>
        )}
      </>
    );
  }
  return (
    <>
      {line.description}
      {line.productCode ? <span style={{ color: "#555" }}> ({line.productCode})</span> : null}
    </>
  );
};

export const PrototypeSheet = ({ prototype }) => {
  const lines = prototype.lines || [];
  const net = lines.reduce((sum, l) => sum + lineNet(l), 0);
  const vat = lines.reduce((sum, l) => sum + lineVat(l), 0);

  return (
    <div style={{ ...PRINT_BASE, padding: "6px" }}>
      <PrintHeader
        title={`QUOTATION PROTOTYPE - ${prototype.name}`}
        subtitle={`${prototype.branchCode} · ${lines.length} line${lines.length === 1 ? "" : "s"}`}
        lines={[prototype.description, prototype.isActive ? null : "*** INACTIVE ***"]}
        branchCode={prototype.branchCode}
      />

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...PRINT_TH, width: "26px" }}>#</th>
            <th style={{ ...PRINT_TH, width: "78px" }}>Type</th>
            <th style={PRINT_TH}>Item</th>
            <th style={{ ...PRINT_TH, width: "42px" }}>Unit</th>
            <th style={{ ...PRINT_TH, ...RIGHT, width: "48px" }}>Qty</th>
            <th style={{ ...PRINT_TH, ...RIGHT, width: "80px" }}>Rate</th>
            <th style={{ ...PRINT_TH, ...RIGHT, width: "44px" }}>VAT %</th>
            <th style={{ ...PRINT_TH, ...RIGHT, width: "72px" }}>VAT Amt</th>
            <th style={{ ...PRINT_TH, ...RIGHT, width: "90px" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, idx) => (
            <tr key={l.lineId ?? idx}>
              <td style={PRINT_TD}>{idx + 1}</td>
              <td style={PRINT_TD}>{quotationLineTypeLabel(l)}</td>
              <td style={PRINT_TD}><ItemCell line={l} /></td>
              <td style={PRINT_TD}>{l.unit || "-"}</td>
              <td style={{ ...PRINT_TD, ...RIGHT }}>{l.qty}</td>
              <td style={{ ...PRINT_TD, ...RIGHT }}>{money(l.rate)}</td>
              <td style={{ ...PRINT_TD, ...RIGHT }}>{Number(l.vatPercent || 0)}</td>
              <td style={{ ...PRINT_TD, ...RIGHT }}>{money(lineVat(l))}</td>
              <td style={{ ...PRINT_TD, ...RIGHT }}>{money(lineNet(l) + lineVat(l))}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <table style={{ marginLeft: "auto", marginTop: "8px", borderCollapse: "collapse" }}>
        <tbody>
          <tr><td style={{ ...PRINT_TD, fontWeight: 700 }}>Sub total</td><td style={{ ...PRINT_TD, ...RIGHT, minWidth: "110px" }}>{money(net)}</td></tr>
          <tr><td style={{ ...PRINT_TD, fontWeight: 700 }}>VAT</td><td style={{ ...PRINT_TD, ...RIGHT }}>{money(vat)}</td></tr>
          <tr><td style={{ ...PRINT_TD, fontWeight: 700, borderBottom: "1.5px solid #000" }}>Total</td><td style={{ ...PRINT_TD, ...RIGHT, fontWeight: 700, borderBottom: "1.5px solid #000" }}>{money(net + vat)}</td></tr>
        </tbody>
      </table>

      <div style={{ fontSize: "9.5px", color: "#555", marginTop: "10px" }}>
        Internal template - rates are the prototype's defaults and may be changed on the quotation raised from it.
      </div>
    </div>
  );
};

const PrototypePrintButton = ({ prototype }) => {
  const print = () => {
    const win = window.open("", "_blank", "width=1000,height=900");
    if (!win) {
      toast.error("Allow pop-ups for this site to print.");
      return;
    }
    try {
      printElement(<PrototypeSheet prototype={prototype} />, { title: `Prototype - ${prototype.name}`, win });
    } catch (e) {
      win.close();
      toast.error(e?.message || "Could not prepare the print");
    }
  };

  return (
    <button type="button" className="btn btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={print}>
      <Printer size={14} /> Print
    </button>
  );
};

export default PrototypePrintButton;
